import styled from 'styled-components';

import { NorlysFont } from '~src/branding';
import { useCustomerContext } from '~src/context/CustomerContext';

import { CustomerInformationNorlys } from '../form/Information/Customer';
import { CustomerDetails, ResidenceInput } from '~src/model';
import { ButtonNorlys } from '~src/components/Button';
import { Heading, Text } from '~src/components/Typography';
import {
  ADDITIONAL_INFORMATION_BULLETPOINTS,
  ADDITIONAL_INFORMATION_DISCLAIMER,
  ADDITIONAL_INFORMATION_INCENTIVE,
  ADDITIONAL_INFORMATION_QUESTIONS,
  ADDITIONAL_INFORMATION_SUBTITLE,
  ADDITIONAL_INFORMATION_TITLE,
  CREATING_OFFER_MESSAGE,
} from './constants';
import { BulletPointsNorlys } from '../BulletPoints';
import { TextWithHighlight } from '../TextWithHighlights';
import { useDisplayContext } from '~src/context/DisplayContext';

import { SelectNorlys } from '../Select';
import { useState } from 'react';

const NorlysTitle = styled.div({ ...NorlysFont.style('heading', 30) });

type Props = {
  isLoading?: boolean;
  onConfirm: (parameters: Partial<CustomerDetails>) => void;
  residence?: Partial<Omit<ResidenceInput, 'address'>>;
};

export const SendManualOfferNorlys = ({ isLoading, onConfirm }: Props) => {
  const [page, setPage] = useState(1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <FirstPage isLoading={isLoading} page={page} setPage={setPage} />

      <SecondPage isLoading={isLoading} page={page} onConfirm={onConfirm} />

      {isLoading ? <Text>{CREATING_OFFER_MESSAGE}</Text> : null}
    </div>
  );
};

type FirstPageProps = Omit<Props, 'onConfirm'> & { page: number; setPage: (page: number) => void };

const FirstPage = ({ isLoading, page, setPage }: FirstPageProps) => {
  const { customer } = useCustomerContext();

  const { firstName, lastName, email, phoneNumber } = customer ?? {};

  const fadeInStyle = page === 1 ? { opacity: 1, display: 'inherit' } : {};
  const fadeOutStyle = page !== 1 ? { opacity: 0, display: 'none' } : {};

  const fadeStyle = { transition: 'opacity 0.5s', opacity: 0, ...fadeInStyle, ...fadeOutStyle };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', ...fadeStyle }}>
      <NorlysTitle>Se hele beregningen</NorlysTitle>

      <CustomerInformationNorlys isLoading={isLoading} />

      <ButtonNorlys
        style={{
          margin: 0,
          alignSelf: 'center',
          padding: '16px 48px',
          textTransform: 'uppercase',
        }}
        type='default'
        isLoading={isLoading}
        disabled={
          !firstName?.length ||
          !lastName?.length ||
          !email?.length ||
          !phoneNumber?.length ||
          (phoneNumber.length > 0 && phoneNumber.length < 8)
        }
        onClick={() => (customer ? setPage(2) : undefined)}
      >
        Fortsæt
      </ButtonNorlys>
    </div>
  );
};

type SecondPageProps = Omit<Props, 'onConfirm'> & {
  page: number;
  onConfirm: (parameters: Partial<CustomerDetails>) => void;
};

const SecondPage = ({ isLoading, page, onConfirm }: SecondPageProps) => {
  const { isMobile } = useDisplayContext();
  const { customer, setCustomer } = useCustomerContext();

  const fadeInStyle = page === 2 ? { opacity: 1 } : {};
  const fadeOutStyle = page !== 2 ? { opacity: 0, display: 'none' } : {};

  const fadeStyle = { transition: 'opacity 0.5s', opacity: 0, ...fadeInStyle, ...fadeOutStyle };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifySelf: 'center',
        textAlign: 'center',
        gap: '32px',
        ...fadeStyle,
      }}
    >
      <Heading style={{ ...NorlysFont.style('heading') }}>
        {TextWithHighlight({ text: ADDITIONAL_INFORMATION_TITLE, highlights: { '2.000 kr.': 'orange' } })}
      </Heading>

      <Heading style={{ ...NorlysFont.style('heading') }} level={2}>
        {TextWithHighlight({ text: ADDITIONAL_INFORMATION_SUBTITLE, highlights: { '2.000 kr.': 'orange' } })}
      </Heading>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
        <div style={{ justifyItems: 'start', textAlign: 'left' }}>
          <div>
            <BulletPointsNorlys entries={ADDITIONAL_INFORMATION_BULLETPOINTS}></BulletPointsNorlys>

            <Heading style={{ ...NorlysFont.style('heading') }} level={4}>
              {ADDITIONAL_INFORMATION_DISCLAIMER}
            </Heading>
          </div>

          <div style={{ marginBottom: 0, marginTop: '30px' }}>
            <Heading style={{ ...NorlysFont.style('heading') }} level={3}>
              {ADDITIONAL_INFORMATION_INCENTIVE}
            </Heading>
          </div>
        </div>

        <div>
          {ADDITIONAL_INFORMATION_QUESTIONS.map(({ label, value, options }) => {
            return (
              <SelectNorlys
                style={{ width: '100%', marginBottom: '8px' }}
                label={label}
                options={options}
                onSelect={option =>
                  setCustomer?.({ ...customer, scoringFields: { ...customer?.scoringFields, [value]: option } })
                }
              />
            );
          })}
        </div>
      </div>
      <ButtonNorlys
        type='callToAction'
        isLoading={isLoading}
        onClick={() => (customer ? onConfirm(customer) : undefined)}
      >
        Fortsæt
      </ButtonNorlys>
    </div>
  );
};
